import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Spinner, Placeholder, Table, Badge, Pagination, Form, Modal, OverlayTrigger, Tooltip, ButtonGroup } from 'react-bootstrap';
import TransactionModal from '../components/TransactionModal';
import TradeGainLossDistribution from '../components/TradeGainLossDistribution';
import EdgeHistoryModal from '../components/EdgeHistoryModal';
import { getTrades, getTradingStatistics, getAllAccounts, deleteTrade, updateTrade } from '../services/api';
import { subscribeToTickerUpdates } from '../services/socketService';
import { formatDate, formatISODate } from '../utils/dateUtils';
import { createLogger } from '../utils/debugLogger';

const logger = createLogger(
  'tradesPage',
  'Trades page real-time updates and order diagnostics',
  false,
  'DEBUG'
);

// Custom styles for the component
const customStyles = `
    /* CSS Variables for consistent theming */
    :root {
        --card-bg: #ffffff;
        --border-color: #dee2e6;
        --shadow-color: rgba(0, 0, 0, 0.1);
        --text-primary: #212529;
        --bg-secondary: #f8f9fa;
        --hover-bg: #f1f5f9;
    }
    
    [data-theme="dark"] {
        --card-bg: #1f2937;
        --border-color: #374151;
        --shadow-color: rgba(0, 0, 0, 0.3);
        --text-primary: #f9fafb;
        --bg-secondary: #374151;
        --hover-bg: #4b5563;
    }

    .trades-empty-icon {
        color: #6c757d;
        opacity: 0.6;
    }
    
    /* Enhanced card styling for maximum contrast and prominence */
    .trades-card {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        transition: all 0.3s ease-in-out;
        position: relative;
    }
    
    .trades-card:hover {
        box-shadow: 0 8px 24px var(--shadow-color) !important;
        transform: translateY(-2px);
    }
    
    /* Light theme card enhancements - much more contrast */
    [data-theme="light"] .trades-card {
        background-color: #ffffff !important;
        border: 2px solid #d1d5db !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 4px 20px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="light"] .trades-card:hover {
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.25) !important;
        border-color: #9ca3af !important;
    }
    
    /* Dark theme card enhancements - much more contrast */
    [data-theme="dark"] .trades-card {
        background-color: #1f2937 !important;
        border: 2px solid #4b5563 !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5) !important;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05), 0 4px 20px rgba(0, 0, 0, 0.5) !important;
    }
    
    [data-theme="dark"] .trades-card:hover {
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
        border-color: #6b7280 !important;
        background-color: #374151 !important;
    }
    
    /* Enhanced table styling for maximum contrast and prominence */
    .trades-table-container {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        overflow: hidden !important;
        transition: all 0.3s ease-in-out;
    }
    
    .trades-table-container:hover {
        box-shadow: 0 8px 24px var(--shadow-color) !important;
    }
    
    /* Light theme table enhancements */
    [data-theme="light"] .trades-table-container {
        background-color: #ffffff !important;
        border: 2px solid #d1d5db !important;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 4px 20px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="light"] .trades-table-container:hover {
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.25) !important;
        border-color: #9ca3af !important;
    }
    
    /* Dark theme table enhancements */
    [data-theme="dark"] .trades-table-container {
        background-color: #1f2937 !important;
        border: 2px solid #4b5563 !important;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05), 0 4px 20px rgba(0, 0, 0, 0.5) !important;
    }
    
    [data-theme="dark"] .trades-table-container:hover {
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
        border-color: #6b7280 !important;
        background-color: #374151 !important;
    }
    
    /* Remove gap at bottom of table */
    .trades-table-container .table {
        margin-bottom: 0 !important;
    }
    
    .trades-table-container .trades-table {
        margin-bottom: 0 !important;
        background-color: var(--card-bg) !important;
    }
    
    /* Enhanced table header styling with hover effects */
    .trades-table-container .trades-table thead th {
        background-color: #f8f9fa !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
        padding: 8px 12px !important;
        transition: all 0.2s ease-in-out !important;
        cursor: pointer !important;
    }
    
    .trades-table-container .trades-table thead th:hover {
        background-color: #e9ecef !important;
        transform: translateY(-1px) !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="light"] .trades-table-container .trades-table thead th {
        background-color: #f8f9fa !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .trades-table-container .trades-table thead th:hover {
        background-color: #e9ecef !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="dark"] .trades-table-container .trades-table thead th {
        background-color: #374151 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .trades-table-container .trades-table thead th:hover {
        background-color: #4b5563 !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3) !important;
    }
    
    /* Enhanced table row styling with hover effects - more prominent and brighter */
    .trades-table-container .trades-table tbody tr {
        border-bottom: 1px solid var(--border-color) !important;
        transition: all 0.3s ease-in-out !important;
        cursor: pointer !important;
        font-size: 0.95rem !important;
    }
    
    .trades-table-container .trades-table tbody tr:hover {}
    [data-theme="light"] .trades-table-container .trades-table tbody tr:hover {}
    [data-theme="dark"] .trades-table-container .trades-table tbody tr:hover {}
    
    /* Enhanced table cell styling with hover effects */
    .trades-table-container .trades-table tbody td {
        padding: 8px 12px !important;
        border-bottom: 1px solid var(--border-color) !important;
        vertical-align: middle !important;
        transition: all 0.3s ease-in-out !important;
    }
    
    .trades-table-container .trades-table tbody tr:hover td {
        color: var(--text-primary) !important;
        font-weight: 500 !important;
        transform: scale(1.02) !important;
    }
    
    [data-theme="light"] .trades-table-container .trades-table tbody td {
        border-bottom: 1px solid #e5e7eb !important;
    }
    
    [data-theme="light"] .trades-table-container .trades-table tbody tr:hover td {
        color: #1e40af !important;
    }
    
    [data-theme="dark"] .trades-table-container .trades-table tbody td {
        border-bottom: 1px solid #374151 !important;
    }
    
    [data-theme="dark"] .trades-table-container .trades-table tbody tr:hover td {
        color: #93c5fd !important;
    }
    
    /* Dark mode support for history dropdown */
    [data-theme="dark"] .ticker-history-dropdown {
        background-color: #2d3748 !important;
        border-color: #4a5568 !important;
        color: #e2e8f0 !important;
    }
    
    [data-theme="dark"] .ticker-history-item {
        color: #e2e8f0 !important;
    }
    
    [data-theme="dark"] .ticker-history-item:hover {
        background-color: #4a5568 !important;
    }
    
    [data-theme="dark"] .ticker-history-remove-btn {
        color: #a0aec0 !important;
    }
    
    [data-theme="dark"] .ticker-history-remove-btn:hover {
        color: #e2e8f0 !important;
    }
    
    [data-theme="dark"] .ticker-history-icon {
        color: #a0aec0 !important;
    }
    
    [data-theme="dark"] .ticker-history-icon:hover {
        color: #e2e8f0 !important;
    }
    
    /* Enhanced sortable table headers - already handled by thead th:hover styles above */
    .sortable-header {
        transition: all 0.2s ease-in-out !important;
    }

    /* Context menu styles */
    .context-menu {
        background-color: var(--card-bg);
        border: 1px solid var(--border-color);
        box-shadow: 0 4px 16px var(--shadow-color);
    }
    
    .context-menu-item {
        color: var(--text-primary);
    }
    
    .context-menu-item:hover {
        background-color: var(--hover-bg);
    }
`;

const VALID_TIME_PERIODS = [
    '1day',
    '2days',
    '3days',
    '1week',
    '2weeks',
    '1month',
    '2months',
    '3months',
    '6months',
    '1year',
    '2years'
];
const VALID_POST_ANALYSIS_FILTERS = ['analyzed', 'empty', 'both'];
const VALID_TRADE_STATUSES = ['all', 'active', 'closed'];

const Trades = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [trades, setTrades] = useState([]);
    const [allTradesForDistribution, setAllTradesForDistribution] = useState([]); // New state for distribution chart
    const [tradingStats, setTradingStats] = useState({});
    const [tradesLoading, setTradesLoading] = useState(true);
    const [statisticsLoading, setStatisticsLoading] = useState(true);
    const [distributionLoading, setDistributionLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedStatus, setSelectedStatus] = useState(() => {
        if (typeof window === 'undefined') {
            return 'closed';
        }

        const storedStatus = localStorage.getItem('tradesSelectedStatus');
        return VALID_TRADE_STATUSES.includes(storedStatus) ? storedStatus : 'closed';
    });
    const [tickerSearch, setTickerSearch] = useState('');
    const [tickerSearchHistory, setTickerSearchHistory] = useState([]);
    const [showTickerHistory, setShowTickerHistory] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(() => {
        if (typeof window === 'undefined') {
            return null;
        }

        return localStorage.getItem('tradesSelectedAccount') || 'all';
    });
    const [, setHighestBalanceAccountId] = useState(null);
    const [selectedTimePeriod, setSelectedTimePeriod] = useState(() => {
        if (typeof window === 'undefined') {
            return '1month';
        }

        const storedPeriod = localStorage.getItem('tradesSelectedTimePeriod');
        return storedPeriod && VALID_TIME_PERIODS.includes(storedPeriod) ? storedPeriod : '1month';
    });
    const [timePeriodMode, setTimePeriodMode] = useState(() => {
        const storedMode = localStorage.getItem('tradesTimePeriodMode');
        return storedMode === 'calendar' ? 'calendar' : 'last';
    });
    const [accounts, setAccounts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => {
        if (typeof window === 'undefined') {
            return 15;
        }

        const storedPageSize = localStorage.getItem('tradesPageSize');
        const parsedPageSize = storedPageSize ? parseInt(storedPageSize, 10) : NaN;

        return Number.isFinite(parsedPageSize) && parsedPageSize > 0 ? parsedPageSize : 15;
    });
    const [pagination, setPagination] = useState({});
    const [sortBy, setSortBy] = useState(() => {
        const savedSortBy = localStorage.getItem('tradesSortBy');
        return savedSortBy || 'closed_at';
    });
    const [sortOrder, setSortOrder] = useState(() => {
        const savedSortOrder = localStorage.getItem('tradesSortOrder');
        return savedSortOrder || 'desc';
    });
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [tradeToDelete, setTradeToDelete] = useState(null);
    const [deletingTrade, setDeletingTrade] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [deleteSuccess, setDeleteSuccess] = useState('');
    const [expectancyExpanded, setExpectancyExpanded] = useState(() => {
        const stored = localStorage.getItem('expectancyStatsExpanded');
        return stored === null ? false : stored === 'true';
    });
    
    // Edge history modal state
    const [showEdgeHistoryModal, setShowEdgeHistoryModal] = useState(false);
    const [edgeHistoryTimePeriod, setEdgeHistoryTimePeriod] = useState('1month');

    // Create stable empty arrays to prevent infinite loops in TransactionModal
    const stableEmptyTickers = useMemo(() => [], []);
    const stableNoOpFunction = useMemo(() => () => {}, []);
    const [postAnalysisFilter, setPostAnalysisFilter] = useState(() => {
        if (typeof window === 'undefined') {
            return 'both';
        }

        const storedFilter = localStorage.getItem('tradesPostAnalysisFilter');
        return storedFilter && VALID_POST_ANALYSIS_FILTERS.includes(storedFilter)
            ? storedFilter
            : 'both';
    });
    const [postAnalysisCounts, setPostAnalysisCounts] = useState({ analyzed: 0, empty: 0, total: 0 });

    // Calculate post-analysis statistics for the Post column header
    const tradeHasPostAnalysis = useCallback((trade) => {
        if (!trade) {
            return false;
        }

        return (
            trade.has_post_analysis ||
            trade.post_analysis_rating != null ||
            (trade.post_analysis_analysis && trade.post_analysis_analysis.trim().length > 0) ||
            (trade.post_analysis_lessons && trade.post_analysis_lessons.trim().length > 0)
        );
    }, []);

    const filteredTrades = useMemo(() => {
        if (!trades || trades.length === 0) {
            return [];
        }

        if (postAnalysisFilter === 'both') {
            return trades;
        }

        return trades.filter((trade) => {
            const analyzed = tradeHasPostAnalysis(trade);
            return postAnalysisFilter === 'analyzed' ? analyzed : !analyzed;
        });
    }, [trades, postAnalysisFilter, tradeHasPostAnalysis]);

    const totalGainLoss = useMemo(() => {
        if (!filteredTrades || filteredTrades.length === 0) {
            return 0;
        }

        return filteredTrades.reduce((sum, trade) => {
            const tradeValue = trade.status === 'CLOSED' ? trade.final_gain_loss : trade.total_gain_loss;
            const numericValue = Number(tradeValue ?? 0);

            return sum + (Number.isFinite(numericValue) ? numericValue : 0);
        }, 0);
    }, [filteredTrades]);

    const [showRatingModal, setShowRatingModal] = useState(false);
    const [ratingTrade, setRatingTrade] = useState(null);
    const [ratingValue, setRatingValue] = useState(0);
    const [showPostModal, setShowPostModal] = useState(false);
    const [postTrade, setPostTrade] = useState(null);
    const [postAnalysisValue, setPostAnalysisValue] = useState('');
    const [postLessonsValue, setPostLessonsValue] = useState('');

    // Context menu state
    const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, trade: null });
    const isWeekendMarketClosed = useMemo(() => {
        if (typeof window === 'undefined') {
            return false;
        }
        const day = new Date().getDay();
        return day === 0 || day === 6;
    }, []);

    const supportsCalendarMode = useCallback((period) => {
        return ['1month', '2months', '3months', '6months', '1year', '2years'].includes(period);
    }, []);

    const getDateRangeDates = useCallback((period, mode = timePeriodMode) => {
        const now = new Date();

        const subtractDays = (days) => {
            const result = new Date(now);
            result.setDate(now.getDate() - days);
            logger.debug(`Trades: subtractDays(${days}) from ${now.toISOString()} = ${result.toISOString()}`);
            return result;
        };

        if (mode === 'calendar' && supportsCalendarMode(period)) {
            const calendarMonthsBack = {
                '1month': 0,
                '2months': 1,
                '3months': 2,
                '6months': 5,
                '1year': 11,
                '2years': 23
            };

            const monthsBack = calendarMonthsBack[period] ?? 0;
            const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            startDate.setMonth(startDate.getMonth() - monthsBack);
            return { startDate, endDate: now };
        }

        switch (period) {
            case '1day':
                return { startDate: subtractDays(1), endDate: now };
            case '2days':
                return { startDate: subtractDays(2), endDate: now };
            case '3days':
                return { startDate: subtractDays(3), endDate: now };
            case '1week':
                return { startDate: subtractDays(7), endDate: now };
            case '2weeks':
                return { startDate: subtractDays(14), endDate: now };
            case '1month':
                return { startDate: subtractDays(30), endDate: now };
            case '2months':
                return { startDate: subtractDays(60), endDate: now };
            case '3months':
                return { startDate: subtractDays(90), endDate: now };
            case '6months':
                return { startDate: subtractDays(180), endDate: now };
            case '1year':
                return { startDate: subtractDays(365), endDate: now };
            case '2years':
                return { startDate: subtractDays(730), endDate: now };
            default:
                return { startDate: subtractDays(30), endDate: now };
        }
    }, [timePeriodMode, supportsCalendarMode]);

    const getDateRangeFromPeriod = useCallback((period, mode = timePeriodMode) => {
        const { startDate, endDate } = getDateRangeDates(period, mode);

        return {
            startDate: formatISODate(startDate),
            endDate: formatISODate(endDate)
        };
    }, [timePeriodMode, getDateRangeDates]);

    const fetchTrades = useCallback(async () => {
        try {
            setTradesLoading(true);
            const dateRange = getDateRangeFromPeriod(selectedTimePeriod, timePeriodMode);

            // Debug logging for date ranges
            logger.debug(`Trades: Fetching with period=${selectedTimePeriod}, start_date=${dateRange.startDate}, end_date=${dateRange.endDate}`);

            const response = await getTrades({
                status: selectedStatus,
                ticker: tickerSearch,
                account_id: selectedAccount && selectedAccount !== 'all' ? selectedAccount : undefined,
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                page: currentPage,
                per_page: pageSize,
                sort_by: sortBy,
                sort_order: sortOrder,
                post_analysis_filter: postAnalysisFilter
            });
            setTrades(response.data.trades);
            setPagination(response.data.pagination);
            if (response.data.post_analysis_counts) {
                const { analyzed, empty, total } = response.data.post_analysis_counts;
                setPostAnalysisCounts({
                    analyzed: analyzed ?? 0,
                    empty: empty ?? 0,
                    total: total ?? 0
                });
            } else {
                setPostAnalysisCounts({ analyzed: 0, empty: 0, total: 0 });
            }
            setError('');
        } catch (err) {
            logger.error('Error fetching trades', err);
            setError('Failed to load trades');
            setPostAnalysisCounts({ analyzed: 0, empty: 0, total: 0 });
        } finally {
            setTradesLoading(false);
        }
    }, [selectedStatus, tickerSearch, selectedAccount, selectedTimePeriod, timePeriodMode, currentPage, pageSize, sortBy, sortOrder, postAnalysisFilter, getDateRangeFromPeriod]);

    const fetchAllTradesForDistribution = useCallback(async () => {
        try {
            setDistributionLoading(true);
            const dateRange = getDateRangeFromPeriod(selectedTimePeriod, timePeriodMode);
            const perPage = 500;
            let page = 1;
            let accumulatedTrades = [];
            let hasMore = true;

            while (hasMore) {
                const response = await getTrades({
                    ticker: tickerSearch,
                    account_id: selectedAccount && selectedAccount !== 'all' ? selectedAccount : undefined,
                    start_date: dateRange.startDate,
                    end_date: dateRange.endDate,
                    per_page: perPage,
                    page,
                    sort_by: 'closed_at',
                    sort_order: 'desc'
                });

                const tradesPage = response.data?.trades || [];
                const pagination = response.data?.pagination;

                accumulatedTrades = accumulatedTrades.concat(tradesPage);

                if (tradesPage.length === 0) {
                    hasMore = false;
                    continue;
                }

                if (pagination) {
                    const shouldContinue = pagination.has_next && accumulatedTrades.length < pagination.total;
                    hasMore = shouldContinue;
                    if (shouldContinue) {
                        page += 1;
                    }
                } else {
                    const shouldContinue = tradesPage.length === perPage;
                    hasMore = shouldContinue;
                    if (shouldContinue) {
                        page += 1;
                    }
                }
            }

            setAllTradesForDistribution(accumulatedTrades);
        } catch (err) {
            logger.error('Error fetching all trades for distribution', err);
            // Don't show error to user since this is for the chart only
        } finally {
            setDistributionLoading(false);
        }
    }, [selectedAccount, selectedTimePeriod, timePeriodMode, tickerSearch, getDateRangeFromPeriod]);

    const fetchTradingStatistics = useCallback(async () => {
        try {
            setStatisticsLoading(true);
            const dateRange = getDateRangeFromPeriod(selectedTimePeriod, timePeriodMode);
            const response = await getTradingStatistics({
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                account_id: selectedAccount && selectedAccount !== 'all' ? selectedAccount : undefined,
                status: selectedStatus
            });
            setTradingStats(response.data);
            
            // Debug logging
            logger.debug('Trades: Received trading statistics', {
              expectancy: response.data?.expectancy,
              expectancy_position_weighted: response.data?.expectancy_position_weighted,
              account_id: selectedAccount && selectedAccount !== 'all' ? selectedAccount : 'all'
            });
        } catch (err) {
            logger.error('Error fetching trading statistics', err);
        } finally {
            setStatisticsLoading(false);
        }
    }, [selectedAccount, selectedStatus, selectedTimePeriod, timePeriodMode, getDateRangeFromPeriod]);

    useEffect(() => {
        // Only fetch trades if selectedAccount is properly set (not null)
        if (selectedAccount !== null) {
            fetchTrades();
        }
    }, [selectedAccount, fetchTrades]);

    useEffect(() => {
        if (selectedAccount !== null) {
            fetchAllTradesForDistribution();
            fetchTradingStatistics();
        }
    }, [selectedAccount, fetchAllTradesForDistribution, fetchTradingStatistics]);


    useEffect(() => {
        fetchAccounts();
        loadTickerSearchHistory();
        // fetchAccounts intentionally omitted: this loads the account list and any
        // navigation-provided preselected account once on mount only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        localStorage.setItem('expectancyStatsExpanded', expectancyExpanded);
    }, [expectancyExpanded]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('tradesPageSize', pageSize);
        }
    }, [pageSize]);

    // Listen for ticker updates via WebSocket
    useEffect(() => {
        logger.debug('Trades: Setting up ticker update subscription');

        if (isWeekendMarketClosed) {
            logger.debug('Trades: Weekend detected - skipping ticker update subscription');
            return () => {};
        }

        const unsubscribe = subscribeToTickerUpdates((data) => {
            logger.debug('Trades: Received ticker update via WebSocket', {
                hasUpdatedTickers: Boolean(data?.updated_tickers?.length > 0),
                updateCount: data?.updated_tickers?.length || 0,
                hasLastUpdateTime: Boolean(data?.latest_update_time),
                isTimestampEvent: data?.message === 'Timestamp update event'
            });

            if (data && data.updated_tickers && data.updated_tickers.length > 0) {
                if (selectedStatus === 'closed') {
                    logger.debug('Trades: Skipping refresh for closed trades filter');
                    return;
                }

                logger.debug(`Trades: Refreshing trades data due to ${data.updated_tickers.length} ticker updates`);
                fetchTrades();
                fetchAllTradesForDistribution();
                fetchTradingStatistics();
            }
        });

        return () => {
            logger.debug('Trades: Cleaning up ticker update subscription');
            unsubscribe();
        };
    }, [selectedStatus, fetchTrades, fetchAllTradesForDistribution, fetchTradingStatistics, isWeekendMarketClosed]);

    // Fetch all trades for distribution chart (without pagination)


    const fetchAccounts = async () => {
        try {
            const response = await getAllAccounts();
            setAccounts(response.data);
            
            // Find account with highest balance (for reference, but don't set as default)
            if (response.data && response.data.length > 0) {
                const highestBalanceAccount = response.data.reduce((highest, current) => {
                    const currentBalance = parseFloat(current.balance || 0);
                    const highestBalance = parseFloat(highest.balance || 0);
                    return currentBalance > highestBalance ? current : highest;
                });
                
                setHighestBalanceAccountId(highestBalanceAccount.account_id);
                
                // Check if there's a preSelectedAccount from navigation state
                const preSelectedAccount = location.state?.preSelectedAccount;
                const storedAccount = typeof window !== 'undefined' ? localStorage.getItem('tradesSelectedAccount') : null;
                const accountExists = (accountId) => {
                    if (accountId === 'all' || accountId === null) {
                        return true;
                    }

                    const numericId = parseInt(accountId, 10);
                    return response.data.some((acc) => acc.account_id === numericId);
                };

                if (preSelectedAccount && accountExists(String(preSelectedAccount))) {
                    const normalized = String(preSelectedAccount);
                    setSelectedAccount(normalized);
                    if (typeof window !== 'undefined') {
                        localStorage.setItem('tradesSelectedAccount', normalized);
                    }
                } else if (storedAccount && accountExists(storedAccount)) {
                    setSelectedAccount(storedAccount);
                } else {
                    setSelectedAccount('all');
                    if (typeof window !== 'undefined') {
                        localStorage.setItem('tradesSelectedAccount', 'all');
                    }
                }
            }
        } catch (err) {
            logger.error('Error fetching accounts', err);
        }
    };

    const loadTickerSearchHistory = () => {
        try {
            const history = localStorage.getItem('tickerSearchHistory');
            if (history) {
                setTickerSearchHistory(JSON.parse(history));
            }
        } catch (err) {
            logger.error('Error loading ticker search history', err);
        }
    };

    const saveTickerSearchHistory = (searchTerm) => {
        if (!searchTerm.trim()) return;
        
        try {
            const history = [...tickerSearchHistory];
            // Remove if already exists
            const existingIndex = history.indexOf(searchTerm.toUpperCase());
            if (existingIndex > -1) {
                history.splice(existingIndex, 1);
            }
            // Add to beginning
            history.unshift(searchTerm.toUpperCase());
            // Keep only last 5
            const newHistory = history.slice(0, 5);
            
            setTickerSearchHistory(newHistory);
            localStorage.setItem('tickerSearchHistory', JSON.stringify(newHistory));
        } catch (err) {
            logger.error('Error saving ticker search history', err);
        }
    };

    const removeFromTickerHistory = (searchTerm) => {
        try {
            const newHistory = tickerSearchHistory.filter(term => term !== searchTerm);
            setTickerSearchHistory(newHistory);
            localStorage.setItem('tickerSearchHistory', JSON.stringify(newHistory));
        } catch (err) {
            logger.error('Error removing from ticker search history', err);
        }
    };

    // Helper function to get account name by ID
    const getAccountNameById = (accountId) => {
        if (!accountId || accountId === 'all') return null;
        logger.debug('Debug - getAccountNameById called with', { accountId, type: typeof accountId });
        logger.debug('Debug - accounts array length', accounts.length);
        logger.debug('Debug - accounts', accounts);
        const account = accounts.find(acc => acc.account_id === parseInt(accountId));
        logger.debug('Debug - found account', account);
        logger.debug('Debug - account name', account?.name);
        return account?.name || null;
    };

    const handleTradeClick = (tradeId) => {
        navigate(`/trades/${tradeId}`);
    };

    const handleNewTrade = () => {
        setShowTransactionModal(true);
    };

    const handleTransactionComplete = async (transactionData) => {
        setShowTransactionModal(false);
        
        // Note: Trades are now automatically created/updated when transactions are made
        // No need for manual autoCreateTradeFromTransaction call
        
        await fetchTrades();
        await fetchAllTradesForDistribution();
        await fetchTradingStatistics();
    };

    const handleDeleteTrade = async (tradeId) => {
        try {
            setDeletingTrade(true);
            setDeleteError('');
            setDeleteSuccess('');
            await deleteTrade(tradeId);
            setShowDeleteConfirmation(false);
            setTradeToDelete(null);
            await fetchTrades();
            await fetchAllTradesForDistribution();
            await fetchTradingStatistics();
            setDeleteSuccess('Trade deleted successfully!');
            // Clear success message after 3 seconds
            setTimeout(() => setDeleteSuccess(''), 3000);
        } catch (err) {
            logger.error('Error deleting trade', err);
            let errorMessage = 'Failed to delete trade';
            if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.response?.status === 400) {
                errorMessage = 'Cannot delete trade with linked transactions';
            } else if (err.response?.status === 404) {
                errorMessage = 'Trade not found';
            }
            setDeleteError(errorMessage);
        } finally {
            setDeletingTrade(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    const formatPercentage = (percent) => {
        const numericValue = Number(percent);

        if (percent === null || percent === undefined || Number.isNaN(numericValue)) {
            return '0.00%';
        }

        if (!Number.isFinite(numericValue)) {
            if (numericValue > 0) return '∞%';
            if (numericValue < 0) return '-∞%';
            return '0.00%';
        }

        return `${numericValue.toFixed(2)}%`;
    };

    const handleStatusFilter = (status) => {
        if (!VALID_TRADE_STATUSES.includes(status)) {
            return;
        }
        setSelectedStatus(status);
        setCurrentPage(1);
        if (typeof window !== 'undefined') {
            localStorage.setItem('tradesSelectedStatus', status);
        }
    };

    const handlePostAnalysisFilterChange = (filterValue) => {
        if (!VALID_POST_ANALYSIS_FILTERS.includes(filterValue)) {
            return;
        }

        setPostAnalysisFilter(filterValue);
        setCurrentPage(1);
        if (typeof window !== 'undefined') {
            localStorage.setItem('tradesPostAnalysisFilter', filterValue);
        }
    };

    const handleTickerSearch = (event) => {
        const value = event.target.value;
        const sanitizedValue = value.toUpperCase();
        setTickerSearch(sanitizedValue);
        setCurrentPage(1);

        // Save to history when user presses Enter or the search is meaningful
        if (event.type === 'keydown' && event.key === 'Enter' && sanitizedValue.trim()) {
            saveTickerSearchHistory(sanitizedValue);
            setShowTickerHistory(false);
        }
    };

    const handleTickerSearchBlur = () => {
        // Save to history when user leaves the input field with a value
        if (tickerSearch.trim()) {
            saveTickerSearchHistory(tickerSearch.trim());
        }
        setShowTickerHistory(false);
    };

    const selectTickerFromHistory = (searchTerm) => {
        setTickerSearch(searchTerm);
        setCurrentPage(1);
        setShowTickerHistory(false);
    };

    const clearTickerSearch = () => {
        setTickerSearch('');
        setCurrentPage(1);
        setShowTickerHistory(false);
    };

    const handleAccountFilter = (accountId) => {
        setSelectedAccount(accountId);
        setCurrentPage(1);
        if (typeof window !== 'undefined') {
            localStorage.setItem('tradesSelectedAccount', accountId);
        }
    };

    const handleTimePeriodFilter = (timePeriod) => {
        setSelectedTimePeriod(timePeriod);
        setCurrentPage(1);
        if (typeof window !== 'undefined') {
            localStorage.setItem('tradesSelectedTimePeriod', timePeriod);
        }
    };

    const handleTimePeriodModeToggle = () => {
        if (!supportsCalendarMode(selectedTimePeriod)) {
            return;
        }

        setTimePeriodMode((prevMode) => (prevMode === 'calendar' ? 'last' : 'calendar'));
    };

    const isCalendarModeSupported = supportsCalendarMode(selectedTimePeriod);

    useEffect(() => {
        if (!isCalendarModeSupported && timePeriodMode !== 'last') {
            setTimePeriodMode('last');
        }
    }, [isCalendarModeSupported, timePeriodMode]);

    useEffect(() => {
        localStorage.setItem('tradesTimePeriodMode', timePeriodMode);
    }, [timePeriodMode]);

    useEffect(() => {
        if (typeof window !== 'undefined' && selectedAccount !== null) {
            localStorage.setItem('tradesSelectedAccount', selectedAccount);
        }
    }, [selectedAccount]);

    useEffect(() => {
        if (typeof window !== 'undefined' && selectedTimePeriod) {
            localStorage.setItem('tradesSelectedTimePeriod', selectedTimePeriod);
        }
    }, [selectedTimePeriod]);

    // Helper function to get period label for display
    const getPeriodDisplayLabel = (period) => {
        const { startDate, endDate } = getDateRangeDates(period, timePeriodMode);

        if (!startDate || !endDate) {
            return '';
        }

        const descriptiveLabels = {
            '1day': 'Last 1 Day',
            '2days': 'Last 2 Days',
            '3days': 'Last 3 Days',
            '1week': 'Last 7 Days',
            '2weeks': 'Last 14 Days'
        };

        if (descriptiveLabels[period]) {
            return descriptiveLabels[period];
        }

        const includeYear = ['6months', '1year', '2years'].includes(period) || startDate.getFullYear() !== endDate.getFullYear();
        const formatOptions = includeYear
            ? { month: 'short', day: 'numeric', year: 'numeric' }
            : { month: 'short', day: 'numeric' };

        const startLabel = startDate.toLocaleDateString('en-US', formatOptions);
        const endLabel = endDate.toLocaleDateString('en-US', formatOptions);

        return `${startLabel} - ${endLabel}`;
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (event) => {
        const newPageSize = parseInt(event.target.value, 10);
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    const handleSort = (column) => {
        let newSortBy, newSortOrder;
        
        if (sortBy === column) {
            // Toggle sort order if clicking the same column
            newSortBy = column;
            newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            // Set new column and default to ascending order
            newSortBy = column;
            newSortOrder = 'asc';
        }
        
        // Update state
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
        
        // Save to localStorage
        localStorage.setItem('tradesSortBy', newSortBy);
        localStorage.setItem('tradesSortOrder', newSortOrder);
        
        setCurrentPage(1); // Reset to first page when sorting
    };

    const getSortIndicator = (column) => {
        if (sortBy === column) {
            return sortOrder === 'asc' ? ' ↑' : ' ↓';
        }
        return '';
    };

    // Context menu handlers
    const handleRowRightClick = (e, trade) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({
            show: true,
            x: e.clientX,
            y: e.clientY,
            trade: trade
        });
    };

    const handleContextMenuAction = (action) => {
        const { trade } = contextMenu;
        if (!trade) return;

        switch (action) {
            case 'delete':
                setTradeToDelete(trade.trade_id);
                setShowDeleteConfirmation(true);
                break;
            case 'rate':
                setRatingTrade(trade);
                setRatingValue(trade.post_analysis_rating || 0);
                setShowRatingModal(true);
                break;
            case 'postAnalysis':
                setPostTrade(trade);
                setPostAnalysisValue(trade.post_analysis_analysis || '');
                setPostLessonsValue(trade.post_analysis_lessons || '');
                setShowPostModal(true);
                break;
            default:
                break;
        }
        setContextMenu({ show: false, x: 0, y: 0, trade: null });
    };

    const handleCloseContextMenu = () => {
        setContextMenu({ show: false, x: 0, y: 0, trade: null });
    };

    return (
        <>
            <style>{customStyles}</style>
            <Container className="mt-1">


                {/* Error Message */}
                {error && (
                    <Row className="mb-3">
                        <Col>
                            <Alert variant="danger">{error}</Alert>
                        </Col>
                    </Row>
                )}

                {/* Delete Success Message */}
                {deleteSuccess && (
                    <Row className="mb-3">
                        <Col>
                            <Alert variant="success" dismissible onClose={() => setDeleteSuccess('')}>
                                {deleteSuccess}
                            </Alert>
                        </Col>
                    </Row>
                )}

                {/* Delete Error Message */}
                {deleteError && (
                    <Row className="mb-3">
                        <Col>
                            <Alert variant="danger" dismissible onClose={() => setDeleteError('')}>
                                {deleteError}
                            </Alert>
                        </Col>
                    </Row>
                )}

                {/* Controls/Filters */}
                <Row className="mb-3">
                    <Col>
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div className="d-flex align-items-center flex-wrap">
                                <div className="me-3 mb-2 position-relative">
                                    <div className="position-relative">
                                        <Form.Control
                                            type="text"
                                            value={tickerSearch}
                                            onChange={handleTickerSearch}
                                            onKeyDown={handleTickerSearch}
                                            onFocus={() => setShowTickerHistory(true)}
                                            onBlur={handleTickerSearchBlur}
                                            size="sm"
                                            style={{ width: '150px', paddingRight: tickerSearchHistory.length > 0 ? '30px' : '12px' }}
                                        />
                                        {tickerSearchHistory.length > 0 && (
                                            <div 
                                                className="position-absolute ticker-history-icon"
                                                style={{ 
                                                    top: '50%', 
                                                    right: '8px', 
                                                    transform: 'translateY(-50%)',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => setShowTickerHistory(!showTickerHistory)}
                                            >
                                                <i className="bi bi-clock-history" style={{ fontSize: '12px' }}></i>
                                            </div>
                                        )}
                                    </div>
                                    {showTickerHistory && tickerSearchHistory.length > 0 && (
                                        <div 
                                            className="position-absolute bg-white border rounded shadow-sm ticker-history-dropdown" 
                                            style={{ 
                                                top: '100%', 
                                                left: 0, 
                                                right: 0, 
                                                zIndex: 1000,
                                                maxHeight: '200px',
                                                overflowY: 'auto'
                                            }}
                                        >
                                            {tickerSearchHistory.map((searchTerm, index) => (
                                                <div 
                                                    key={index}
                                                    className="d-flex justify-content-between align-items-center px-2 py-1 hover-bg-light ticker-history-item"
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseDown={() => selectTickerFromHistory(searchTerm)}
                                                >
                                                    <span className="small">{searchTerm}</span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm text-muted p-0 ticker-history-remove-btn"
                                                        style={{ fontSize: '12px' }}
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation();
                                                            removeFromTickerHistory(searchTerm);
                                                        }}
                                                    >
                                                        <i className="bi bi-x"></i>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {tickerSearch && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={clearTickerSearch}
                                        size="sm"
                                        className="me-3 mb-2"
                                    >
                                        <i className="bi bi-x"></i>
                                    </Button>
                                )}
                                <div className="me-3 mb-2">
                                    <Form.Select
                                        size="sm"
                                        value={selectedAccount || ''}
                                        onChange={(e) => handleAccountFilter(e.target.value)}
                                        style={{ width: '180px' }}
                                    >
                                        <option value="all">All Accounts</option>
                                        {accounts.map((account) => (
                                            <option key={account.account_id} value={account.account_id}>
                                                {account.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                                <div className="me-3 mb-2">
                                    <Form.Select
                                        size="sm"
                                        value={selectedTimePeriod}
                                        onChange={(e) => handleTimePeriodFilter(e.target.value)}
                                        style={{ width: '140px' }}
                                    >
                                        <option value="1day">1 Day</option>
                                        <option value="2days">2 Days</option>
                                        <option value="3days">3 Days</option>
                                        <option value="1week">1 Week</option>
                                        <option value="2weeks">2 Weeks</option>
                                        <option value="1month">1 Month</option>
                                        <option value="2months">2 Months</option>
                                        <option value="3months">3 Months</option>
                                        <option value="6months">6 Months</option>
                                        <option value="1year">1 Year</option>
                                        <option value="2years">2 Years</option>
                                    </Form.Select>
                                </div>
                                <div className="me-3 mb-2">
                                    <Button
                                        variant={selectedStatus === 'all' ? 'primary' : 'outline-secondary'}
                                        onClick={() => handleStatusFilter('all')}
                                        size="sm"
                                        className="me-2"
                                    >
                                        All Trades
                                    </Button>
                                    <Button
                                        variant={selectedStatus === 'active' ? 'success' : 'outline-secondary'}
                                        onClick={() => handleStatusFilter('active')}
                                        size="sm"
                                        className="me-2"
                                    >
                                        Active
                                    </Button>
                                    <Button
                                        variant={selectedStatus === 'closed' ? 'secondary' : 'outline-secondary'}
                                        onClick={() => handleStatusFilter('closed')}
                                        size="sm"
                                        className="me-2"
                                    >
                                        Closed
                                    </Button>
                                </div>
                            </div>
                            
                            <Button
                                variant="primary"
                                onClick={handleNewTrade}
                                size="sm"
                                className="mb-2"
                            >
                                <i className="bi bi-plus-circle me-1"></i>
                                Start New Trade
                            </Button>
                        </div>
                    </Col>
                </Row>

                {/* Row 1: Key Trading Statistics */}
                <Row className="mb-2 g-2">
                    <Col xs={12}>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                <div className="d-flex align-items-center">
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 me-2 text-decoration-none"
                                        style={{ minWidth: 'auto' }}
                                        onClick={() => {
                                            setExpectancyExpanded((prev) => {
                                                localStorage.setItem('expectancyStatsExpanded', !prev);
                                                return !prev;
                                            });
                                        }}
                                    >
                                        <i className={`bi bi-chevron-${expectancyExpanded ? 'down' : 'right'}`}></i>
                                    </button>
                                    <span className="fs-5 mb-0 fw-bold">Trading Statistics</span>
                                    <small className="text-muted ms-2">({getPeriodDisplayLabel(selectedTimePeriod)})</small>
                                </div>
                                <div className="d-flex align-items-center ms-auto">
                                    <span
                                        className={`small me-2 ${isCalendarModeSupported ? 'text-muted' : 'text-muted opacity-50'}`}
                                    >
                                        Last
                                    </span>
                                    <Form.Check
                                        type="switch"
                                        id="time-period-mode-toggle"
                                        checked={timePeriodMode === 'calendar'}
                                        onChange={handleTimePeriodModeToggle}
                                        disabled={!isCalendarModeSupported}
                                        className="me-2"
                                    />
                                    <span
                                        className={`small ${isCalendarModeSupported ? 'text-muted' : 'text-muted opacity-50'}`}
                                    >
                                        Calendar
                                    </span>
                                </div>
                            </div>
                            {!expectancyExpanded && (
                                <div className="small text-muted">
                                    {statisticsLoading ? (
                                        <span>Loading...</span>
                                    ) : (
                                        <>
                                            <span className="text-muted" style={{ fontSize: '1rem', fontWeight: '600' }}>
                                                {selectedAccount && selectedAccount !== 'all'
                                                    ? `Account: ${getAccountNameById(selectedAccount)} | `
                                                    : 'All Accounts | '}
                                            </span>
                                            <span
                                                className={`text-${(tradingStats.expectancy_position_weighted || 0) >= 0 ? 'success' : 'danger'}`}
                                            >
                                                Expectancy: {typeof tradingStats.expectancy_position_weighted === 'number'
                                                    ? formatPercentage(tradingStats.expectancy_position_weighted)
                                                    : 'Not available'}
                                            </span>
                                            {' | '}
                                            <span className="text-success">
                                                Win Rate: {tradingStats.closed_trades > 0
                                                    ? ((tradingStats.winning_trades / tradingStats.closed_trades) * 100).toFixed(1)
                                                    : 0}%
                                            </span>
                                            {' | '}
                                            <span className="text-primary">
                                                Total: {tradingStats.total_trades || 0} trades
                                            </span>
                                            {' | '}
                                            <span className={`text-${(tradingStats.total_gain_loss || 0) >= 0 ? 'success' : 'danger'}`}>
                                                P&L: {formatCurrency(tradingStats.total_gain_loss)}
                                            </span>
                                        </>
                                    )}
                                </div>
                            )}
                    </Col>
                </Row>
                {expectancyExpanded && (
                    <>
                        <Row className="mb-2 g-2">
                            {/* Left Column: Winning Stats */}
                            <Col xl={4} lg={4} md={12} sm={12} xs={12}>
                                <Row className="g-2">
                                    <Col xs={6}>
                                        <Card className="h-100 trades-card">
                                            <Card.Body className="p-2 text-center">
                                                <Card.Title className="fs-7 mb-1 fw-semibold">Win Rate</Card.Title>
                                                <small className="text-muted d-block mb-1">Completed Trades</small>
                                                {statisticsLoading ? (
                                                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                        <Placeholder xs={8} className="fs-6" />
                                                    </Placeholder>
                                                ) : (
                                                    <Card.Text className="fs-6 mb-0 fw-bold text-success">
                                                        {formatPercentage(tradingStats.win_rate)}
                                                    </Card.Text>
                                                )}
                                                {statisticsLoading ? (
                                                    <div style={{height: "12px"}}></div>
                                                ) : (
                                                    <small className="d-block text-muted fw-semibold">
                                                        {tradingStats.winning_trades || 0}W / {tradingStats.losing_trades || 0}L
                                                    </small>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col xs={6}>
                                        <Card className="h-100 trades-card">
                                            <Card.Body className="p-2 text-center">
                                                <Card.Title className="fs-7 mb-1 fw-semibold">Avg Winning %</Card.Title>
                                                <small className="text-muted d-block mb-1">Per Winning Trade</small>
                                                {statisticsLoading ? (
                                                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                        <Placeholder xs={7} className="fs-6" />
                                                    </Placeholder>
                                                ) : (
                                                    <Card.Text className="fs-6 mb-0 fw-bold text-success">
                                                        {formatPercentage(tradingStats.average_gain_pct)}
                                                    </Card.Text>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col xs={12}>
                                        <Card 
                                            className="h-100 trades-card"
                                            style={{ cursor: tradingStats.best_trade ? 'pointer' : 'default' }}
                                            onClick={() => {
                                                if (tradingStats.best_trade) {
                                                    navigate(`/trade/${tradingStats.best_trade.trade_id}`);
                                                }
                                            }}
                                        >
                                            <Card.Body className="p-2 text-center">
                                                <Card.Title className="fs-7 mb-1 fw-semibold">Best Trade</Card.Title>
                                                <small className="text-muted d-block mb-1">Highest % Gain</small>
                                                {statisticsLoading ? (
                                                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                        <Placeholder xs={7} className="fs-6" />
                                                    </Placeholder>
                                                ) : (
                                                    <Card.Text className={`fs-6 mb-0 fw-bold text-${(tradingStats.best_trade_pct || 0) >= 0 ? 'success' : 'danger'}`}>
                                                        {formatPercentage(tradingStats.best_trade_pct || 0)}
                                                    </Card.Text>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </Col>

                            {/* Center Column: Expectancy */}
                            <Col xl={4} lg={4} md={12} sm={12} xs={12}>
                                <Card className="h-100 trades-card">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 me-2 text-decoration-none"
                                                style={{ minWidth: 'auto' }}
                                                onClick={() => {
                                                    setExpectancyExpanded((prev) => {
                                                        localStorage.setItem('expectancyStatsExpanded', !prev);
                                                        return !prev;
                                                    });
                                                }}
                                            >
                                                <i className={`bi bi-chevron-${expectancyExpanded ? 'down' : 'right'}`}></i>
                                            </button>
                                            <span className="fs-5 mb-0 fw-bold">
                                                Expectancy
                                                <i 
                                                    className="bi bi-info-circle ms-1" 
                                                    style={{ fontSize: '0.8rem', cursor: 'help' }}
                                                    title="Trading edge (expectancy) from CLOSED trades only. Shows raw expectancy and a secondary position-weighted expectancy that accounts for position size via standard size multiples."
                                                ></i>
                                            </span>
                                        </div>
                                        <small className="text-muted d-block mb-0">Trading Edge</small>
                                    </Card.Header>
                                    {expectancyExpanded && (
                                        <Card.Body 
                                            className="p-3 text-center"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setShowEdgeHistoryModal(true)}
                                        >
                                            {statisticsLoading ? (
                                                <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                    <Placeholder xs={8} className="fs-3" />
                                                </Placeholder>
                                            ) : (
                                                <>
                                                    <div className="text-muted" style={{fontSize: '1rem', height: "20px", fontWeight: '600'}}>
                                                        {selectedAccount && selectedAccount !== 'all' 
                                                            ? `Account: ${getAccountNameById(selectedAccount)}`
                                                            : 'All Accounts'
                                                        }
                                                    </div>
                                                    <div className="text-muted small" style={{height: "18px"}}>{getPeriodDisplayLabel(selectedTimePeriod)}</div>
                                                    <div className="text-muted small" style={{fontSize: '0.7rem', height: "16px"}}>Closed Trades Only</div>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        delay={{ show: 200, hide: 100 }}
                                                        overlay={
                                                            <Tooltip id="expectancy-tooltip">E = Σ(w·r) / Σw, where r = trade % return and w = capital ÷ standard size</Tooltip>
                                                        }
                                                        trigger={["hover", "focus", "click"]}
                                                    >
                                                        <div>
                                                            <Card.Text className={`fs-2 mb-1 fw-bold text-${(tradingStats.expectancy_position_weighted || 0) >= 0 ? 'success' : 'danger'}`}>
                                                                {typeof tradingStats.expectancy_position_weighted === 'number' ? formatPercentage(tradingStats.expectancy_position_weighted) : 'Not available'}
                                                            </Card.Text>
                                                        </div>
                                                    </OverlayTrigger>
                                                    <small className={`d-block fs-7 text-${(tradingStats.expectancy_position_weighted || 0) >= 0 ? 'success' : 'danger'} fw-semibold`}>
                                                        {(tradingStats.expectancy_position_weighted || 0) >= 0 ? '✓ Profitable Strategy' : '✗ Losing Strategy'}
                                                    </small>
                                                </>
                                            )}
                                        </Card.Body>
                                    )}
                                </Card>
                            </Col>

                            {/* Right Column: Losing Stats */}
                            <Col xl={4} lg={4} md={12} sm={12} xs={12}>
                                <Row className="g-2">
                                    <Col xs={6}>
                                        <Card className="h-100 trades-card">
                                            <Card.Body className="p-2 text-center">
                                                <Card.Title className="fs-7 mb-1 fw-semibold">Loss Rate</Card.Title>
                                                <small className="text-muted d-block mb-1">Completed Trades</small>
                                                {statisticsLoading ? (
                                                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                        <Placeholder xs={8} className="fs-6" />
                                                    </Placeholder>
                                                ) : (
                                                    <Card.Text className="fs-6 mb-0 fw-bold text-danger">
                                                        {formatPercentage(tradingStats.closed_trades > 0 ? (tradingStats.losing_trades / tradingStats.closed_trades * 100) : 0)}
                                                    </Card.Text>
                                                )}
                                                {statisticsLoading ? (
                                                    <div style={{height: "12px"}}></div>
                                                ) : (
                                                    <small className="d-block text-muted fw-semibold">
                                                        {tradingStats.losing_trades || 0}L / {tradingStats.winning_trades || 0}W
                                                    </small>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col xs={6}>
                                        <Card className="h-100 trades-card">
                                            <Card.Body className="p-2 text-center">
                                                <Card.Title className="fs-7 mb-1 fw-semibold">Avg Losing %</Card.Title>
                                                <small className="text-muted d-block mb-1">Per Losing Trade</small>
                                                {statisticsLoading ? (
                                                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                        <Placeholder xs={7} className="fs-6" />
                                                    </Placeholder>
                                                ) : (
                                                    <Card.Text className="fs-6 mb-0 fw-bold text-danger">
                                                        {formatPercentage(tradingStats.average_loss_pct)}
                                                    </Card.Text>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col xs={12}>
                                        <Card 
                                            className="h-100 trades-card"
                                            style={{ cursor: tradingStats.worst_trade ? 'pointer' : 'default' }}
                                            onClick={() => {
                                                if (tradingStats.worst_trade) {
                                                    navigate(`/trade/${tradingStats.worst_trade.trade_id}`);
                                                }
                                            }}
                                        >
                                            <Card.Body className="p-2 text-center">
                                                <Card.Title className="fs-7 mb-1 fw-semibold">Worst Trade</Card.Title>
                                                <small className="text-muted d-block mb-1">Lowest % Loss</small>
                                                {statisticsLoading ? (
                                                    <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                        <Placeholder xs={7} className="fs-6" />
                                                    </Placeholder>
                                                ) : (
                                                    <Card.Text className={`fs-6 mb-0 fw-bold text-${(tradingStats.worst_trade_pct || 0) >= 0 ? 'success' : 'danger'}`}>
                                                        {formatPercentage(tradingStats.worst_trade_pct || 0)}
                                                    </Card.Text>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        {/* Row 2: Holding Days Statistics */}
                        <Row className="mb-2 g-2">
                            <Col xl={4} lg={4} md={6} sm={12} xs={12}>
                                <Card className="h-100 trades-card">
                                    <Card.Body className="p-2 text-center">
                                        <Card.Title className="fs-7 mb-1 fw-semibold">Avg Winning Days</Card.Title>
                                        <small className="text-muted d-block mb-1">Let your winners run</small>
                                        {statisticsLoading ? (
                                            <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                <Placeholder xs={6} className="fs-6" />
                                            </Placeholder>
                                        ) : (
                                            <Card.Text className="fs-6 mb-0 fw-bold text-success">
                                                {tradingStats.average_winning_days || 0} days
                                            </Card.Text>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xl={4} lg={4} md={6} sm={12} xs={12}>
                                <Card className="h-100 trades-card">
                                    <Card.Body className="p-2 text-center">
                                        <Card.Title className="fs-7 mb-1 fw-semibold">Win/Loss Ratio</Card.Title>
                                        <small className="text-muted d-block mb-1">Reward/Risk</small>
                                        {statisticsLoading ? (
                                            <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                <Placeholder xs={6} className="fs-6" />
                                            </Placeholder>
                                        ) : (
                                            <Card.Text className={`fs-6 mb-0 fw-bold ${
                                                tradingStats.average_gain_pct !== undefined && tradingStats.average_loss_pct !== undefined ?
                                                    (Math.abs(tradingStats.average_gain_pct) < Math.abs(tradingStats.average_loss_pct) ? 'text-danger' : 'text-success') : 'text-primary'
                                            }`}>
                                                {(() => {
                                                    logger.debug('Win/Loss Ratio Debug', {
                                                        win_loss_ratio: tradingStats.win_loss_ratio,
                                                        type: typeof tradingStats.win_loss_ratio,
                                                        avg_gain: tradingStats.average_gain_pct,
                                                        avg_loss: tradingStats.average_loss_pct
                                                    });
                                                    if (tradingStats.win_loss_ratio === undefined || tradingStats.win_loss_ratio === null) {
                                                        return 'N/A';
                                                    } else if (tradingStats.win_loss_ratio === Infinity) {
                                                        return '∞';
                                                    } else if (tradingStats.win_loss_ratio === 0) {
                                                        return '0.00';
                                                    } else {
                                                        // Simple calculation: win%/loss% with minus sign if win% < abs(loss%)
                                                        const winPct = tradingStats.average_gain_pct || 0;
                                                        const lossPct = Math.abs(tradingStats.average_loss_pct || 0);
                                                        const ratio = winPct / lossPct;
                                                        return winPct < lossPct ? `-${ratio.toFixed(2)}` : ratio.toFixed(2);
                                                    }
                                                })()}
                                            </Card.Text>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xl={4} lg={4} md={6} sm={12} xs={12}>
                                <Card className="h-100 trades-card">
                                    <Card.Body className="p-2 text-center">
                                        <Card.Title className="fs-7 mb-1 fw-semibold">Avg Losing Days</Card.Title>
                                        <small className="text-muted d-block mb-1">Cut your losers quick</small>
                                        {statisticsLoading ? (
                                            <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                <Placeholder xs={6} className="fs-6" />
                                            </Placeholder>
                                        ) : (
                                            <Card.Text className="fs-6 mb-0 fw-bold text-danger">
                                                {tradingStats.average_losing_days || 0} days
                                            </Card.Text>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Row 3: Overview Metrics */}
                        <Row className="mb-2 g-2">
                            <Col xl={2} lg={3} md={4} sm={6} xs={12}>
                                <Card className="h-100 trades-card">
                                    <Card.Body className="p-2 text-center">
                                        <Card.Title className="fs-7 mb-1 fw-semibold">Total Trades</Card.Title>
                                        <small className="text-muted d-block mb-1">All Time</small>
                                        {statisticsLoading ? (
                                            <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                <Placeholder xs={6} className="fs-6" />
                                            </Placeholder>
                                        ) : (
                                            <Card.Text className="fs-6 mb-0 text-primary fw-bold">
                                                {tradingStats.total_trades || 0}
                                            </Card.Text>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col xl={2} lg={3} md={4} sm={6} xs={12}>
                                <Card className="h-100 trades-card">
                                    <Card.Body className="p-2 text-center">
                                        <Card.Title className="fs-7 mb-1 fw-semibold">Active Trades</Card.Title>
                                        <small className="text-muted d-block mb-1">Open Positions</small>
                                        {statisticsLoading ? (
                                            <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                <Placeholder xs={6} className="fs-6" />
                                            </Placeholder>
                                        ) : (
                                            <Card.Text className="fs-6 mb-0 fw-bold text-info">
                                                {tradingStats.active_trades || 0}
                                            </Card.Text>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col xl={2} lg={3} md={4} sm={6} xs={12}>
                                <Card className="h-100 trades-card">
                                    <Card.Body className="p-2 text-center">
                                        <Card.Title className="fs-7 mb-1 fw-semibold">Total P&L</Card.Title>
                                        <small className="text-muted d-block mb-1">All Trades</small>
                                        {statisticsLoading ? (
                                            <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                <Placeholder xs={7} className="fs-6" />
                                            </Placeholder>
                                        ) : (
                                            <Card.Text className={`fs-6 mb-0 fw-bold text-${(tradingStats.total_gain_loss || 0) >= 0 ? 'success' : 'danger'}`}>
                                                {formatCurrency(tradingStats.total_gain_loss)}
                                            </Card.Text>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col xl={2} lg={3} md={4} sm={6} xs={12}>
                                <Card className="h-100 trades-card">
                                    <Card.Body className="p-2 text-center">
                                        <Card.Title className="fs-7 mb-1 fw-semibold">Portfolio Value</Card.Title>
                                        <small className="text-muted d-block mb-1">All Trades Period</small>
                                        {statisticsLoading ? (
                                            <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                <Placeholder xs={7} className="fs-6" />
                                            </Placeholder>
                                        ) : (
                                            <Card.Text className="fs-6 mb-0 fw-bold text-primary">
                                                {formatCurrency(allTradesForDistribution.reduce((sum, trade) => sum + (trade.current_value || 0), 0))}
                                            </Card.Text>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col xl={2} lg={3} md={4} sm={6} xs={12}>
                                <Card className="h-100 trades-card">
                                    <Card.Body className="p-2 text-center">
                                        <Card.Title className="fs-7 mb-1 fw-semibold">Realized P&L</Card.Title>
                                        <small className="text-muted d-block mb-1">Closed Trades</small>
                                        {statisticsLoading ? (
                                            <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                <Placeholder xs={7} className="fs-6" />
                                            </Placeholder>
                                        ) : (
                                            <Card.Text className={`fs-6 mb-0 fw-bold text-${(tradingStats.closed_total_gain_loss || 0) >= 0 ? 'success' : 'danger'}`}>
                                                {formatCurrency(tradingStats.closed_total_gain_loss)}
                                            </Card.Text>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col xl={2} lg={3} md={4} sm={6} xs={12}>
                                <Card className="h-100 trades-card">
                                    <Card.Body className="p-2 text-center">
                                        <Card.Title className="fs-7 mb-1 fw-semibold">Unrealized P&L</Card.Title>
                                        <small className="text-muted d-block mb-1">Active Trades</small>
                                        {statisticsLoading ? (
                                            <Placeholder as={Card.Text} animation="glow" className="mb-0">
                                                <Placeholder xs={7} className="fs-6" />
                                            </Placeholder>
                                        ) : (
                                            <Card.Text className={`fs-6 mb-0 fw-bold text-${(tradingStats.active_total_gain_loss || 0) >= 0 ? 'success' : 'danger'}`}>
                                                {formatCurrency(tradingStats.active_total_gain_loss)}
                                            </Card.Text>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}

                {/* Distribution Chart */}
                <Row className="mb-4">
                    <Col>
                        <TradeGainLossDistribution
                            trades={allTradesForDistribution}
                            loading={distributionLoading}
                            periodLabel={getPeriodDisplayLabel(selectedTimePeriod)}
                            timePeriodMode={timePeriodMode}
                        />
                    </Col>
                </Row>

                {/* Trades Table */}
                <Row>
                    <Col>
                        <div className="trades-table-container">
                            <div className="d-flex flex-column flex-sm-row justify-content-sm-end align-items-sm-center px-3 pt-3 pb-1">
                                <ButtonGroup size="sm" className="ms-sm-auto" aria-label="Filter trades by post analysis status">
                                    <Button
                                        variant={postAnalysisFilter === 'analyzed' ? 'primary' : 'secondary'}
                                        onClick={() => handlePostAnalysisFilterChange('analyzed')}
                                    >
                                        Analyzed ({postAnalysisCounts.analyzed})
                                    </Button>
                                    <Button
                                        variant={postAnalysisFilter === 'empty' ? 'primary' : 'secondary'}
                                        onClick={() => handlePostAnalysisFilterChange('empty')}
                                    >
                                        Empty ({postAnalysisCounts.empty})
                                    </Button>
                                    <Button
                                        variant={postAnalysisFilter === 'both' ? 'primary' : 'secondary'}
                                        onClick={() => handlePostAnalysisFilterChange('both')}
                                    >
                                        Both ({postAnalysisCounts.total})
                                    </Button>
                                </ButtonGroup>
                            </div>
                            <div className="table-responsive">
                                <Table className="mb-0 trades-table">
                                        <thead>
                                            <tr>
                                                <th 
                                                    className="py-1 px-3 sortable-header" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('symbol')}
                                                >
                                                    Symbol{getSortIndicator('symbol')}
                                                </th>
                                                <th 
                                                    className="py-1 px-3 sortable-header" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('accounts')}
                                                >
                                                    Account(s){getSortIndicator('accounts')}
                                                </th>
                                                <th 
                                                    className="py-1 px-3 sortable-header" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('status')}
                                                >
                                                    Status{getSortIndicator('status')}
                                                </th>
                                                <th 
                                                    className="py-1 px-3 text-end sortable-header" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('shares')}
                                                >
                                                    Shares{getSortIndicator('shares')}
                                                </th>
                                                <th 
                                                    className="py-1 px-3 text-end sortable-header" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('current_value')}
                                                >
                                                    Value{getSortIndicator('current_value')}
                                                </th>
                                                <th 
                                                    className="py-1 px-3 text-end sortable-header" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('gain_loss')}
                                                >
                                                    G/L${getSortIndicator('gain_loss')}
                                                </th>
                                                <th 
                                                    className="py-1 px-3 text-end sortable-header" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('gain_loss_pct')}
                                                >
                                                    G/L%{getSortIndicator('gain_loss_pct')}
                                                </th>
                                                <th 
                                                    className="py-1 px-3 text-center sortable-header" 
                                                    style={{ cursor: 'pointer', minWidth: '90px' }}
                                                    onClick={() => handleSort('post_analysis_rating')}
                                                >
                                                    Rating{getSortIndicator('post_analysis_rating')}
                                                </th>
                                                <th 
                                                    className="py-1 px-3 text-center sortable-header" 
                                                    style={{ cursor: 'pointer', minWidth: '80px' }}
                                                    onClick={() => handleSort('has_post_analysis')}
                                                >
                                                    Post ({postAnalysisCounts.analyzed}/{postAnalysisCounts.total}){getSortIndicator('has_post_analysis')}
                                                </th>
                                                <th 
                                                    className="py-1 px-3 text-center sortable-header" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('days_held')}
                                                >
                                                    Days{getSortIndicator('days_held')}
                                                </th>
                                                <th 
                                                    className="py-1 px-3 text-center sortable-header" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('created_at')}
                                                >
                                                    Created{getSortIndicator('created_at')}
                                                </th>
                                                <th 
                                                    className="py-1 px-3 text-center sortable-header" 
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSort('closed_at')}
                                                >
                                                    Closed{getSortIndicator('closed_at')}
                                                </th>

                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tradesLoading ? (
                                                // Loading placeholders
                                                [...Array(5)].map((_, index) => (
                                                    <tr key={index}>
                                                        <td className="py-1 px-3">
                                                            <Placeholder as="div" animation="glow">
                                                                <Placeholder xs={4} />
                                                            </Placeholder>
                                                        </td>
                                                        <td className="py-1 px-3">
                                                            <Placeholder as="div" animation="glow">
                                                                <Placeholder xs={6} />
                                                            </Placeholder>
                                                        </td>
                                                        <td className="py-1 px-3">
                                                            <Placeholder as="div" animation="glow">
                                                                <Placeholder xs={6} />
                                                            </Placeholder>
                                                        </td>
                                                        <td className="py-1 px-3 text-end">
                                                            <Placeholder as="div" animation="glow">
                                                                <Placeholder xs={5} />
                                                            </Placeholder>
                                                        </td>
                                                        <td className="py-1 px-3 text-end">
                                                            <Placeholder as="div" animation="glow">
                                                                <Placeholder xs={7} />
                                                            </Placeholder>
                                                        </td>
                                                        <td className="py-1 px-3 text-end">
                                                            <Placeholder as="div" animation="glow">
                                                                <Placeholder xs={6} />
                                                            </Placeholder>
                                                        </td>
                                                        <td className="py-1 px-3 text-end">
                                                            <Placeholder as="div" animation="glow">
                                                                <Placeholder xs={4} />
                                                            </Placeholder>
                                                        </td>
                                                        <td className="py-1 px-3 text-center">
                                                            <Placeholder as="div" animation="glow">
                                                                <Placeholder xs={4} />
                                                            </Placeholder>
                                                        </td>
                                                        <td className="py-1 px-3 text-center">
                                                            <Placeholder as="div" animation="glow">
                                                                <Placeholder xs={5} />
                                                            </Placeholder>
                                                        </td>
                                                        <td className="py-1 px-3 text-center">
                                                            <Placeholder as="div" animation="glow">
                                                                <Placeholder xs={3} />
                                                            </Placeholder>
                                                        </td>
                                                        <td className="py-1 px-3 text-center">
                                                            <Placeholder as="div" animation="glow">
                                                                <Placeholder xs={3} />
                                                            </Placeholder>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : filteredTrades.length > 0 ? (
                                                <>
                                                    {filteredTrades.map((trade) => (
                                                    <tr 
                                                        key={trade.trade_id}
                                                        onClick={() => handleTradeClick(trade.trade_id)}
                                                        onContextMenu={(e) => handleRowRightClick(e, trade)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td className="py-1 px-3">
                                                            <div className="fw-bold">{trade.ticker_symbol}</div>
                                                        </td>
                                                        <td className="py-1 px-3">
                                                            <div className="small">
                                                                {trade.accounts && trade.accounts.length > 0 ? (
                                                                    trade.accounts.map((account, index) => (
                                                                        <div key={account.account_id} className="text-muted">
                                                                            {account.name}
                                                                            {index < trade.accounts.length - 1 && <br />}
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="py-1 px-3">
                                                            <Badge bg={trade.status === 'ACTIVE' ? 'success' : 'secondary'}>
                                                                {trade.status}
                                                            </Badge>
                                                        </td>
                                                        <td className="py-1 px-3 text-end">
                                                            {trade.total_shares?.toLocaleString() || '0'}
                                                        </td>
                                                        <td className="py-1 px-3 text-end">
                                                            {formatCurrency(trade.current_value)}
                                                        </td>
                                                        <td className="py-1 px-3 text-end">
                                                            <div className={`text-${(trade.status === 'CLOSED' ? (trade.final_gain_loss || 0) : (trade.total_gain_loss || 0)) >= 0 ? 'success' : 'danger'}`}>
                                                                <div className="fw-bold">
                                                                    {formatCurrency(trade.status === 'CLOSED' ? trade.final_gain_loss : trade.total_gain_loss)}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-1 px-3 text-end">
                                                            <div className={`text-${(trade.status === 'CLOSED' ? (trade.final_gain_loss || 0) : (trade.total_gain_loss || 0)) >= 0 ? 'success' : 'danger'}`}>
                                                                <div className="fw-bold">
                                                                    {formatPercentage(trade.status === 'CLOSED' ? trade.final_gain_loss_pct : trade.total_gain_loss_pct)}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        {/* Rating column */}
                                                        <td className="py-1 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                            <div title={trade.post_analysis_rating ? `${trade.post_analysis_rating} stars` : 'No rating'}>
                                                                {[1,2,3,4,5].map((star) => (
                                                                    <i
                                                                        key={star}
                                                                        className={`bi ${star <= (trade.post_analysis_rating || 0) ? 'bi-star-fill text-warning' : 'bi-star text-muted'} me-1`}
                                                                        style={{ cursor: 'pointer' }}
                                                                        onClick={() => { setRatingTrade(trade); setRatingValue(trade.post_analysis_rating || 0); setShowRatingModal(true); }}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </td>
                                                        {/* Post-analysis indicator column */}
                                                        <td className="py-1 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                            {(() => {
                                                                const hasPost = Boolean(
                                                                    (trade.post_analysis_analysis && trade.post_analysis_analysis.trim().length > 0) ||
                                                                    (trade.post_analysis_lessons && trade.post_analysis_lessons.trim().length > 0)
                                                                );
                                                                return (
                                                                    <span
                                                                        role="button"
                                                                        title={hasPost ? 'View/Edit Post Analysis' : 'Add Post Analysis'}
                                                                        onClick={() => {
                                                                            setPostTrade(trade);
                                                                            setPostAnalysisValue(trade.post_analysis_analysis || '');
                                                                            setPostLessonsValue(trade.post_analysis_lessons || '');
                                                                            setShowPostModal(true);
                                                                        }}
                                                                    >
                                                                        <i className={`bi fs-5 ${hasPost ? 'bi-journal-text text-info' : 'bi-journal text-muted'}`} />
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                                                                                <td className="py-1 px-3 text-center">
                                                            {trade.days_held || 0}
                                                        </td>
                                                        <td className="py-1 px-3 text-center">
                                                            <small>{formatDate(trade.created_at)}</small>
                                                        </td>
                                                        <td className="py-1 px-3 text-center">
                                                            <small>{trade.closed_at ? formatDate(trade.closed_at) : '-'}</small>
                                                        </td>

                                                    </tr>
                                                    ))}
                                                    <tr className="table-active" style={{ cursor: 'default' }}>
                                                        <td className="py-2 px-3 fw-semibold" colSpan={5}>
                                                            Total Gain/Loss
                                                        </td>
                                                        <td className={`py-2 px-3 text-end fw-bold text-${totalGainLoss >= 0 ? 'success' : 'danger'}`}>
                                                            {formatCurrency(totalGainLoss)}
                                                        </td>
                                                        <td colSpan={6} />
                                                    </tr>
                                                </>
                                            ) : (
                                                <tr>
                                                    <td colSpan="12" className="text-center py-4">
                                                        <div className="text-muted">
                                                            <i className="bi bi-graph-up fs-1 d-block mb-2 trades-empty-icon"></i>
                                                            <p className="mb-2">
                                                                {(() => {
                                                                    let message = '';
                                                                    const filters = [];
                                                                    
                                                                    if (tickerSearch) {
                                                                        filters.push(`ticker "${tickerSearch}"`);
                                                                    }
                                                                    
                                                                    if (selectedAccount && selectedAccount !== 'all') {
                                                                        const accountName = accounts.find(acc => acc.account_id === parseInt(selectedAccount))?.name || 'Unknown Account';
                                                                        filters.push(`account "${accountName}"`);
                                                                    }
                                                                    
                                                                    if (selectedStatus !== 'all') {
                                                                        filters.push(`${selectedStatus} trades`);
                                                                    }

                                                                    if (postAnalysisFilter === 'analyzed') {
                                                                        filters.push('analyzed trades');
                                                                    } else if (postAnalysisFilter === 'empty') {
                                                                        filters.push('unrated or unnoted trades');
                                                                    }
                                                                    
                                                                    // Add time period to filters
                                                                    const timePeriodLabels = {
                                                                        '1day': '1 day',
                                                                        '2days': '2 days',
                                                                        '3days': '3 days',
                                                                        '1week': '1 week',
                                                                        '2weeks': '2 weeks',
                                                                        '1month': '1 month',
                                                                        '2months': '2 months',
                                                                        '3months': '3 months',
                                                                        '6months': '6 months',
                                                                        '1year': '1 year',
                                                                        '2years': '2 years'
                                                                    };
                                                                    filters.push(`last ${timePeriodLabels[selectedTimePeriod] || '3 months'}`);
                                                                    
                                                                    if (filters.length > 0) {
                                                                        message = `No ${filters.join(' and ')} found`;
                                                                    } else {
                                                                        message = 'No trades found';
                                                                    }
                                                                    
                                                                    return message;
                                                                })()}
                                                            </p>
                                                            {selectedStatus === 'all' && !tickerSearch && (!selectedAccount || selectedAccount === 'all') && selectedTimePeriod === '1month' && (
                                                                <Button 
                                                                    variant="primary" 
                                                                    size="sm"
                                                                    onClick={handleNewTrade}
                                                                >
                                                                    Start Your First Trade
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </Col>
                    </Row>

                {/* Enhanced Pagination */}
                {tradesLoading ? (
                    <Row className="mt-3">
                        <Col>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <Placeholder as="span" animation="glow" className="me-2">
                                        <Placeholder xs={6} />
                                    </Placeholder>
                                    <Placeholder as={Form.Select} animation="glow" style={{ width: '80px' }} />
                                    <Placeholder as="span" animation="glow" className="ms-3">
                                        <Placeholder xs={8} />
                                    </Placeholder>
                                </div>
                                <Placeholder.Button variant="primary" xs={5} />
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <Row className="mt-3">
                        <Col>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <span className="me-2">Trades per page:</span>
                                    <Form.Select 
                                        size="sm" 
                                        value={pageSize} 
                                        onChange={handlePageSizeChange}
                                        style={{ width: '80px' }}
                                    >
                                        <option value="10">10</option>
                                        <option value="15">15</option>
                                        <option value="20">20</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </Form.Select>
                                    <span className="ms-3">
                                        {pagination.total > 0 && pagination.page ? (
                                            <>
                                                Showing {(pagination.page - 1) * pageSize + 1} to {Math.min(pagination.page * pageSize, pagination.total)} of {pagination.total}{' '}
                                                {postAnalysisFilter === 'both' ? 'trades' : `${postAnalysisFilter === 'analyzed' ? 'analyzed' : 'empty'} trades`}
                                            </>
                                        ) : postAnalysisFilter === 'both' ? (
                                            'No trades found'
                                        ) : (
                                            `No ${postAnalysisFilter === 'analyzed' ? 'analyzed' : 'empty'} trades found`
                                        )}
                                    </span>
                                </div>
                                
                                {pagination.pages > 1 && (
                                    <Pagination size="sm">
                                    <Pagination.First 
                                        onClick={() => handlePageChange(1)} 
                                        disabled={pagination.page === 1}
                                    />
                                    <Pagination.Prev 
                                        onClick={() => handlePageChange(Math.max(1, pagination.page - 1))} 
                                        disabled={!pagination.has_prev}
                                    />
                                    
                                    {/* Page Numbers */}
                                    {(() => {
                                        const items = [];
                                        const maxItems = 7; // Maximum number of page items to show
                                        
                                        // Add first page
                                        items.push(
                                            <Pagination.Item 
                                                key={1} 
                                                active={pagination.page === 1}
                                                onClick={() => handlePageChange(1)}
                                            >
                                                1
                                            </Pagination.Item>
                                        );
                                        
                                        // Calculate start and end pages
                                        let startPage = Math.max(2, pagination.page - 2);
                                        let endPage = Math.min(pagination.pages - 1, pagination.page + 2);
                                        
                                        // Adjust for edge cases
                                        if (pagination.page <= 3) {
                                            endPage = Math.min(pagination.pages - 1, maxItems - 2);
                                        } else if (pagination.page >= pagination.pages - 2) {
                                            startPage = Math.max(2, pagination.pages - maxItems + 2);
                                        }
                                        
                                        // Add ellipsis after first page if needed
                                        if (startPage > 2) {
                                            items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
                                        }
                                        
                                        // Add intermediate pages
                                        for (let i = startPage; i <= endPage; i++) {
                                            items.push(
                                                <Pagination.Item 
                                                    key={i} 
                                                    active={pagination.page === i}
                                                    onClick={() => handlePageChange(i)}
                                                >
                                                    {i}
                                                </Pagination.Item>
                                            );
                                        }
                                        
                                        // Add ellipsis before last page if needed
                                        if (endPage < pagination.pages - 1) {
                                            items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
                                        }
                                        
                                        // Add last page if there are more than 1 page
                                        if (pagination.pages > 1) {
                                            items.push(
                                                <Pagination.Item 
                                                    key={pagination.pages} 
                                                    active={pagination.page === pagination.pages}
                                                    onClick={() => handlePageChange(pagination.pages)}
                                                >
                                                    {pagination.pages}
                                                </Pagination.Item>
                                            );
                                        }
                                        
                                        return items;
                                    })()}
                                    
                                    <Pagination.Next 
                                        onClick={() => handlePageChange(Math.min(pagination.pages, pagination.page + 1))} 
                                        disabled={!pagination.has_next}
                                    />
                                    <Pagination.Last 
                                        onClick={() => handlePageChange(pagination.pages)} 
                                        disabled={pagination.page === pagination.pages}
                                    />
                                </Pagination>
                                )}
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Transaction Modal for starting new trades */}
                <TransactionModal
                    show={showTransactionModal}
                    onHide={() => setShowTransactionModal(false)}
                    accountId={null} // Don't specify account - let user choose
                    accountTickers={stableEmptyTickers} // Stable empty array
                    availableTickers={stableEmptyTickers} // Stable empty array  
                    setAvailableTickers={stableNoOpFunction} // Stable no-op function
                    onTransactionComplete={handleTransactionComplete}
                />

                {/* Delete Confirmation Modal */}
                <Modal
                    show={showDeleteConfirmation}
                    onHide={() => {
                        setShowDeleteConfirmation(false);
                        setDeleteError('');
                        setTradeToDelete(null);
                    }}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Deletion</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to delete this trade? This action cannot be undone.
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteConfirmation(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={() => tradeToDelete && handleDeleteTrade(tradeToDelete)} disabled={deletingTrade}>
                            {deletingTrade ? (
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            ) : (
                                'Delete Trade'
                            )}
                        </Button>
                    </Modal.Footer>
                    {deleteError && (
                        <Alert variant="danger" className="mt-3">
                            {deleteError}
                        </Alert>
                    )}
                    {deleteSuccess && (
                        <Alert variant="success" className="mt-3">
                            {deleteSuccess}
                        </Alert>
                    )}
                </Modal>

                {/* Edge History Modal */}
                <EdgeHistoryModal
                    show={showEdgeHistoryModal}
                    onHide={() => setShowEdgeHistoryModal(false)}
                    accountId={selectedAccount}
                    accountName={getAccountNameById(selectedAccount)}
                    timePeriod={edgeHistoryTimePeriod}
                    onTimePeriodChange={setEdgeHistoryTimePeriod}
                    accounts={accounts}
                    onAccountChange={(newAccountId) => {
                        setSelectedAccount(newAccountId);
                        // The modal will automatically reload data due to accountId change
                    }}
                />

                {/* Rating Modal */}
                <Modal show={showRatingModal} onHide={() => setShowRatingModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Rate Trade {ratingTrade?.ticker_symbol}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="d-flex justify-content-center">
                            {[1,2,3,4,5].map((star) => (
                                <i
                                    key={star}
                                    className={`bi ${star <= (ratingValue || 0) ? 'bi-star-fill text-warning' : 'bi-star text-muted'} fs-3 me-2`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setRatingValue(star)}
                                />
                            ))}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowRatingModal(false)}>Cancel</Button>
                        <Button variant="outline-danger" onClick={async () => {
                            try {
                                if (ratingTrade) {
                                    await updateTrade(ratingTrade.trade_id, { post_analysis_rating: null });
                                    setTrades((prev) => {
                                        const updated = prev.map((t) =>
                                            t.trade_id === ratingTrade.trade_id ? { ...t, post_analysis_rating: null } : t
                                        );
                                        if (sortBy === 'post_analysis_rating') {
                                            return [...updated].sort((a, b) => {
                                                const av = a.post_analysis_rating ?? 0;
                                                const bv = b.post_analysis_rating ?? 0;
                                                return sortOrder === 'asc' ? av - bv : bv - av;
                                            });
                                        }
                                        return updated;
                                    });
                                    setRatingValue(0);
                                }
                            } finally {
                                setShowRatingModal(false);
                                setRatingTrade(null);
                            }
                        }}>Reset</Button>
                        <Button variant="primary" onClick={async () => {
                            try {
                                if (ratingTrade) {
                                    await updateTrade(ratingTrade.trade_id, { post_analysis_rating: ratingValue });
                                    setTrades((prev) => {
                                        const updated = prev.map((t) =>
                                            t.trade_id === ratingTrade.trade_id ? { ...t, post_analysis_rating: ratingValue } : t
                                        );
                                        if (sortBy === 'post_analysis_rating') {
                                            return [...updated].sort((a, b) => {
                                                const av = a.post_analysis_rating ?? 0;
                                                const bv = b.post_analysis_rating ?? 0;
                                                return sortOrder === 'asc' ? av - bv : bv - av;
                                            });
                                        } else if (sortBy === 'has_post_analysis') {
                                            // Reuse the canonical tradeHasPostAnalysis predicate (also used for
                                            // the Post column header count and the Analyzed/Not Analyzed
                                            // filter) instead of a local redefinition -- a prior local
                                            // redefinition here omitted post_analysis_rating, so saving a
                                            // rating from THIS modal didn't move the trade in has_post_analysis
                                            // sort order, even though a rating is exactly what was just set.
                                            return [...updated].sort((a, b) => {
                                                const av = tradeHasPostAnalysis(a) ? 1 : 0;
                                                const bv = tradeHasPostAnalysis(b) ? 1 : 0;
                                                return sortOrder === 'asc' ? av - bv : bv - av;
                                            });
                                        }
                                        return updated;
                                    });
                                }
                            } finally {
                                setShowRatingModal(false);
                                setRatingTrade(null);
                            }
                        }}>Save</Button>
                    </Modal.Footer>
                </Modal>

                {/* Post Analysis Modal */}
                <Modal show={showPostModal} onHide={() => setShowPostModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Post Analysis {postTrade ? `- ${postTrade.ticker_symbol}` : ''}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Analysis</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                value={postAnalysisValue}
                                onChange={(e) => setPostAnalysisValue(e.target.value)}
                                placeholder="Reflect on the trade: setup quality, entry/exit, emotions, execution..."
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Lessons</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                value={postLessonsValue}
                                onChange={(e) => setPostLessonsValue(e.target.value)}
                                placeholder="What will you do better next time? Rules, signals, context, risk, size..."
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPostModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={async () => {
                            try {
                                if (postTrade) {
                                    await updateTrade(postTrade.trade_id, {
                                        post_analysis_analysis: postAnalysisValue,
                                        post_analysis_lessons: postLessonsValue
                                    });
                                    setTrades((prev) => {
                                        const updated = prev.map((t) =>
                                            t.trade_id === postTrade.trade_id
                                                ? { ...t, post_analysis_analysis: postAnalysisValue, post_analysis_lessons: postLessonsValue }
                                                : t
                                        );
                                        if (sortBy === 'has_post_analysis') {
                                            // Same canonical tradeHasPostAnalysis predicate as the Rating
                                            // modal's Save handler above -- see the comment there.
                                            return [...updated].sort((a, b) => {
                                                const av = tradeHasPostAnalysis(a) ? 1 : 0;
                                                const bv = tradeHasPostAnalysis(b) ? 1 : 0;
                                                return sortOrder === 'asc' ? av - bv : bv - av;
                                            });
                                        }
                                        return updated;
                                    });
                                }
                            } finally {
                                setShowPostModal(false);
                                setPostTrade(null);
                            }
                        }}>Save</Button>
                    </Modal.Footer>
                </Modal>

                {/* Context Menu */}
                {contextMenu.show && (
                    <div
                        className="position-fixed context-menu rounded"
                        style={{
                            left: contextMenu.x,
                            top: contextMenu.y,
                            zIndex: 1050,
                            minWidth: '150px'
                        }}
                        onClick={handleCloseContextMenu}
                    >
                        <div className="p-1">
                            <div
                                className="px-3 py-2 text-decoration-none context-menu-item d-flex align-items-center"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleContextMenuAction('rate')}
                            >
                                <i className="bi bi-star me-2"></i>
                                Rate Trade
                            </div>
                            <div
                                className="px-3 py-2 text-decoration-none context-menu-item d-flex align-items-center"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleContextMenuAction('postAnalysis')}
                            >
                                <i className="bi bi-journal-text me-2"></i>
                                Post Analysis
                            </div>
                            <hr className="my-1" />
                            <div
                                className="px-3 py-2 text-decoration-none text-danger context-menu-item d-flex align-items-center"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleContextMenuAction('delete')}
                            >
                                <i className="bi bi-trash me-2"></i>
                                Delete Trade
                            </div>
                        </div>
                    </div>
                )}

                {/* Click outside to close context menu */}
                {contextMenu.show && (
                    <div
                        className="position-fixed"
                        style={{
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 1040
                        }}
                        onClick={handleCloseContextMenu}
                    />
                )}
            </Container>
        </>
    );
};

export default Trades; 
